/**
 * Shadow-injected CSS for Troubleshooting (`rtk-debugger`) — strings only (CRA has no raw CSS import).
 * Human-readable mirror: `rtk-debugger-layout.css`.
 *
 * @type {string} Outer `rtk-debugger` shell — stable width for sidebar + main.
 */
export const RTK_DEBUGGER_SHELL_SHADOW_CSS = `
/* Shell: rtk-debugger — stable width across Audio / Video / Screenshare / System */
:host {
  box-sizing: border-box;
  min-width: min(52rem, 100%);
  color: rgb(var(--rtk-colors-text-1000)) !important;
}

aside button {
  color: rgb(var(--rtk-colors-text-900)) !important;
}

aside button.active {
  color: rgb(var(--rtk-colors-text-1000)) !important;
}

header h3 {
  color: rgb(var(--rtk-colors-text-1000)) !important;
}

main {
  flex: 1 1 0;
  min-width: min(46rem, 100%);
  max-width: 100%;
  overflow: auto;
}

main > rtk-debugger-audio,
main > rtk-debugger-video,
main > rtk-debugger-screenshare,
main > rtk-debugger-system {
  flex: 1 1 auto;
  align-self: stretch;
  min-width: 0;
  max-width: 100%;
}

:host([size='sm']) main {
  min-width: 0;
}
`;

/**
 * Inner tab panels clip body copy (`:host { overflow: hidden }` in kit CSS). Inject into each panel shadow.
 *
 * @type {string}
 */
export const RTK_DEBUGGER_TAB_PANEL_SHADOW_CSS = `
/* Tab panels: rtk-debugger-audio | video | screenshare | system */
:host {
  overflow-x: visible !important;
  overflow-y: auto !important;
  max-width: 100%;
  min-width: 0;
}

.tab-body {
  overflow-x: visible !important;
  overflow-y: auto !important;
  justify-content: flex-start !important;
  height: auto !important;
  max-height: min(70vh, 100%);
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

#header {
  flex-wrap: wrap;
  gap: var(--rtk-space-2, 8px);
}

.section-body.missing-stats {
  padding-inline: var(--rtk-space-2, 8px);
}

.section-body.missing-stats span {
  display: block;
  max-width: 100%;
  color: rgb(var(--rtk-colors-text-1000)) !important;
}

/*
 * Contrast: kit uses muted text-700 / alpha for descriptions and metric values — fails on light or tinted
 * modal surfaces. Bind body copy to full-opacity RTK text tokens (flow from meeting design system).
 * Do not override .status.* verdict hues on quality labels.
 */
.tab-body,
.status-container {
  color: rgb(var(--rtk-colors-text-1000)) !important;
}

.section-header > span:first-child {
  color: rgb(var(--rtk-colors-text-1000)) !important;
}

.battery-cell.label strong,
.network-cell.label strong {
  color: rgb(var(--rtk-colors-text-1000)) !important;
}

.battery-cell.label .description,
.network-cell.label .description {
  color: rgb(var(--rtk-colors-text-900)) !important;
}

.battery-cell.value span.value,
.network-cell.value span.value,
.battery-cell .value,
.network-cell .value {
  color: rgb(var(--rtk-colors-text-1000)) !important;
}

/* System tab: kit CSS sets battery-table overflow hidden — clips long Battery Level / Charging Status copy */
.section-body.battery-table,
.battery-table {
  overflow: visible !important;
}

/*
 * Kit uses a single-row flex + space-between; long label copy steals width and the value column
 * collapses to a thin strip at the right edge (clipped by host overflow). One column: label block,
 * then full-width value block.
 */
.battery-row {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--rtk-space-2, 8px);
  align-items: start;
}

.battery-cell {
  min-width: 0;
  max-width: 100%;
}

.battery-cell.label .description,
.battery-cell.value .value {
  overflow-wrap: anywhere;
  word-break: break-word;
  max-width: 100%;
}

.battery-cell.value {
  align-items: flex-start !important;
  text-align: left !important;
}

.battery-cell.value span:not(.value),
.battery-cell.value span.value {
  max-width: 100%;
  overflow-wrap: anywhere;
  text-align: left !important;
}

/* Audio / Video / Screenshare: same kit pattern as battery rows — .network-table + flex space-between */
.section-body.network-table,
.network-table {
  overflow: visible !important;
}

.network-row {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--rtk-space-2, 8px);
  align-items: start;
}

.network-cell {
  min-width: 0;
  max-width: 100%;
}

.network-cell.label .description,
.network-cell.value .value {
  overflow-wrap: anywhere;
  word-break: break-word;
  max-width: 100%;
}

.network-cell.value {
  align-items: flex-start !important;
  text-align: left !important;
}

.network-cell.value span:not(.value),
.network-cell.value span.value {
  max-width: 100%;
  overflow-wrap: anywhere;
  text-align: left !important;
}
`;

/**
 * `rtk-dialog` wraps Troubleshooting (`rtk-dialog-manager` → `rtk-dialog` → slotted `rtk-debugger`).
 * Kit CSS caps `#dialog` at max-width 100% of a narrow ancestor (~614px observed), which clips the shell.
 * Only inject into dialogs that actually host `rtk-debugger` so other modals keep default sizing.
 *
 * @type {string}
 */
export const RTK_DEBUGGER_DIALOG_SHADOW_CSS = `
#dialog {
  box-sizing: border-box !important;
  min-width: min(52rem, 95vw) !important;
  width: min(52rem, 95vw) !important;
  max-width: min(52rem, 95vw) !important;
}
`;
